/* Painting on the matrix.
   The notation is still the only thing a chart is written from. A stroke reads the three
   fields, moves one hand, and writes them back, so the grid is a second way to type and not
   a second source of truth: they cannot disagree. */

import { ORDER, WRITTEN, r3, type Action, type WrittenAction } from "./hands";
import { notation, parseNotation, type Notes } from "./notation";
import type { Cell, Cells } from "./spot";

type WeightMaps = Record<WrittenAction, Record<string, number>>;

/** The three notation strings as weight maps. Throws what the parser throws. */
export function mapsFrom(notes: Partial<Notes>): WeightMaps {
  return {
    allin: parseNotation(notes.allin).weights,
    raise: parseNotation(notes.raise).weights,
    call: parseNotation(notes.call).weights,
  };
}

/** Weight maps back to notation, in the canonical form notation() writes. */
export function notesFrom(maps: WeightMaps): Notes {
  const cells: Cells = {};
  for (const a of WRITTEN) for (const [h, w] of Object.entries(maps[a])) (cells[h] ??= {})[a] = w;
  return {
    allin: notation({ cells }, "allin"),
    raise: notation({ cells }, "raise"),
    call: notation({ cells }, "call"),
  };
}

/** One hand trimmed to fit 100%, by the rule applyNotes applies to all of them: the action
 *  just painted is served in full and the others take what is left, in All-In, Raise, Call
 *  order. */
export function fitHand(maps: WeightMaps, hand: string, first?: string | null): void {
  const lead = WRITTEN.find((a) => a === first);
  const order = lead ? [lead, ...WRITTEN.filter((a) => a !== lead)] : WRITTEN;
  let used = 0;
  for (const a of order) {
    const w = maps[a][hand] ?? 0;
    if (!w) continue;
    const got = Math.min(w, Math.max(1 - used, 0));
    if (got > 0.0005) {
      maps[a][hand] = r3(got);
      used += got;
    } else delete maps[a][hand];
  }
}

/** Which action a click paints and at what weight. "erase" clears the hand. */
export interface Stroke {
  action: WrittenAction | "erase";
  /** A percentage of the times hero holds the hand here. */
  weight?: number;
  /** Drop the hand from every action. */
  erase?: boolean;
  /** Drop the hand from the painted action alone. */
  only?: boolean;
}

/** One stroke: the three notation strings with `hand` moved. */
export function strokeNotes(notes: Partial<Notes>, hand: string, stroke: Stroke): Notes {
  const { action, weight = 100, only } = stroke;
  const maps = mapsFrom(notes);
  if (stroke.erase || action === "erase") for (const a of WRITTEN) delete maps[a][hand];
  else if (only) delete maps[action][hand];
  else maps[action][hand] = weight / 100;
  fitHand(maps, hand, only ? null : action);
  return notesFrom(maps);
}

/* The action one roll of the randomizer names for a hand. The bands stack down from 100 in
   ORDER, so the strongest action sits at the top of the range: an all-in played 5% of the
   time is 95-99. Weights are stored to three decimals and need not reach 1 exactly, so the
   last band swallows whatever sliver is left rather than leaving a roll with no answer. */
export function pickByRandom(dist: Cell, rnd: number): Action | null {
  let top = 100;
  let last: Action | null = null;
  for (const a of ORDER) {
    const w = dist[a];
    if (!w) continue;
    top -= w * 100;
    last = a;
    if (rnd >= top - 1e-9) return a;
  }
  return last;
}
